import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin, Phone, EnvelopeSimple, Clock, CaretDown, ArrowRight, SealCheck, WhatsappLogo } from "@phosphor-icons/react";
import { Reveal, Overline } from "@/components/Reveal";
import { useLang } from "@/i18n";

const inputCls =
  "w-full border-b border-navy/25 bg-transparent py-3 text-base font-light text-navy placeholder:text-navy/35 outline-none transition-colors duration-300 focus:border-champagne";
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.25em] text-navy/50";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: 0, message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        type: t.contact.form.types[form.type],
        message: form.message
      });
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  const info = [
    { icon: MapPin, label: t.contact.addressLabel, value: t.contact.address, testid: "contact-address" },
    { icon: Phone, label: t.contact.phoneLabel, value: t.contact.phone, testid: "contact-phone" },
    { icon: WhatsappLogo, label: "WhatsApp", value: t.contact.whatsappCta, href: "https://api.whatsapp.com/send?phone=393759730405", testid: "contact-whatsapp" },
    { icon: EnvelopeSimple, label: t.contact.emailLabel, value: t.contact.email, testid: "contact-email" },
    { icon: Clock, label: t.contact.hoursLabel, value: t.contact.hours, testid: "contact-hours" }
  ];

  return (
    <section id="contatti" data-testid="contact-section" className="border-t border-champagne/20 bg-ivory py-28 md:py-40">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:gap-24 lg:px-12">
        <Reveal>
          <Overline>{t.contact.overline}</Overline>
          <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-navy md:text-5xl lg:text-6xl">
            {t.contact.title}
          </h2>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-navy/70 md:text-lg">{t.contact.text}</p>

          <div className="mt-14 space-y-8">
            {info.map((row) => (
              <div key={row.testid} data-testid={row.testid} className="flex items-start gap-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-champagne/40">
                  <row.icon size={20} weight="light" className="text-champagne" />
                </span>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-navy/45">{row.label}</p>
                  {row.href ? (
                    <a href={row.href} target="_blank" rel="noopener noreferrer" data-testid={`${row.testid}-link`} className="mt-1 block font-serif text-xl font-light text-navy transition-colors duration-300 hover:text-champagne">
                      {row.value}
                    </a>
                  ) : (
                    <p className="mt-1 font-serif text-xl font-light text-navy">{row.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="border border-champagne/25 bg-ivory p-8 md:p-12">
            {status === "sent" ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                data-testid="contact-success-message"
                className="flex min-h-[420px] flex-col items-center justify-center text-center"
              >
                <SealCheck size={56} weight="light" className="text-champagne" />
                <h3 className="mt-6 font-serif text-3xl font-light text-navy">{t.contact.success.title}</h3>
                <p className="mt-3 max-w-xs text-sm font-light leading-relaxed text-navy/60">{t.contact.success.text}</p>
                <button
                  data-testid="contact-again-button"
                  onClick={() => { setStatus("idle"); setForm({ name: "", email: "", phone: "", type: 0, message: "" }); }}
                  className="mt-8 border border-navy/30 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:border-champagne hover:text-champagne"
                >
                  {t.contact.success.again}
                </button>
              </motion.div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className={labelCls}>{t.contact.form.name}</label>
                    <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelCls}>{t.contact.form.email}</label>
                    <input id="contact-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={set("email")} className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-phone" className={labelCls}>{t.contact.form.phone}</label>
                    <input id="contact-phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
                  </div>
                  <div className="relative">
                    <label htmlFor="contact-type" className={labelCls}>{t.contact.form.typeLabel}</label>
                    <select
                      id="contact-type"
                      data-testid="contact-type-select"
                      value={form.type}
                      onChange={set("type")}
                      className={`${inputCls} appearance-none pr-8`}
                    >
                      {(t.contact.form.types || []).map((ty, i) => (
                        <option key={ty} value={i}>{ty}</option>
                      ))}
                    </select>
                    <CaretDown size={16} className="pointer-events-none absolute bottom-4 right-1 text-navy/50" />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className={labelCls}>{t.contact.form.message}</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t.contact.form.placeholder}
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="contact-submit-button"
                  className="group flex w-full items-center justify-center gap-3 bg-navy px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-champagne hover:text-navy disabled:opacity-60"
                >
                  {status === "sending" ? t.contact.form.sending : t.contact.form.submit}
                  <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </motion.button>
                {status === "error" && (
                  <p data-testid="contact-error-message" className="text-center text-sm font-light text-red-800">
                    {t.contact.form.error}
                  </p>
                )}
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
